import React, { Component, ReactNode } from 'react';
import './Chat.scss';
import { connect } from 'react-redux';
import { Api, unreadState$ } from '../../api';
import { ApplicationState } from '../../app/store';
import { ChatMessage } from '../../models/ChatMessage';
import { ChatContainer } from '../../features/chat/ChatContainer';
import { ChatSendMessage } from '../../features/chat/ChatSendMessage';
import { NotificationHelper } from '../../helpers/NotificationHelper';
import { SingleUser, UserAccountRoleEnum, UserRelationRoleEnum } from '../../api/types';
import { ChatUsers } from '../../features/chat/ChatUsers';
import { ChatHeader } from '../../features/chat/ChatHeader';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import { Icon } from '../../features/icon/Icon';
import { DossierGroup, EDossierGroupType } from '../../models/DossierGroup';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Subscription } from 'rxjs';
import { DashboardLayout } from '../../layouts/dashboard-layout/DashboardLayout';
import { IDossierV2 } from '../../models/DossierV2';
import { DossierMember, DossierMemberInterface } from '../../models/DossierMember';
import { User } from '../../models/User';
import { UnreadStatus } from '../../models/UnreadStatus';
import { Search } from '../../features/search/Search';
import { ISplitLayoutItem, SplitSubLayout } from '../../layouts/split-sub-layouts/SplitSubLayout';
import { ERouterPath } from '../../features/router/ERouterPath';
import classNames from 'classnames';
import Button from 'react-bootstrap/esm/Button';
import { NewGroupForm } from './NewGroupForm';
import { ConfirmModal } from '../../features/confirm-modal/ConfirmModal';
import { AddPersonToGroupForm } from './AddPersonToGroupForm';
import { RemovePersonFromGroupForm } from './RemovePersonFromGroupForm copy';
import { ActivityHelper } from '../../helpers/ActivityHelper';

interface ChatProps extends RouteComponentProps {
  dossier?: IDossierV2;
  relation?: DossierMemberInterface;
  user?: SingleUser;
}

interface ChatState {
  unreadCounts: UnreadStatus;
  unreadSubscription?: Subscription;

  sendingMessage?: boolean;

  conversations: DossierGroup[];
  loadingConversations: boolean;
  loadingConversationsFailed?: boolean;

  chatMessages: ChatMessage[];
  currentConversation?: DossierGroup;
  dossierRelations?: DossierMember[];
  loadingChat: boolean;
  loadingChatFailed?: boolean;

  initialConversationId?: number;

  updateLoopRef?: ReturnType<typeof setTimeout>;

  searchKeys?: string;

  createNewGroup?: boolean;
  addPersonToGroup?: boolean;
  removePersonFromGroup?: boolean;

  archiveGroup?: DossierGroup;
}

class NoFileSelectedError extends Error {
  constructor() {
    super('No file selected');
  }
}

class MissingUserError extends Error {
  constructor() {
    super('No user selected');
  }
}

class Chat extends Component<ChatProps, ChatState> {
  private static readonly UPDATE_INTERVAL = 5000;

  constructor(props: ChatProps) {
    super(props);
    this.state = {
      unreadCounts: new UnreadStatus({ chat: {} }),

      chatMessages: [],
      loadingChat: true,

      conversations: [],
      loadingConversations: false,
      currentConversation: undefined,

      initialConversationId: this.getQueryConversationId(this.props.location.search),
    };
  }

  getQueryConversationId(query: string): number | undefined {
    for (const substring of query.split('conversation=')) {
      const id = +substring;
      if (!isNaN(id)) {
        return id;
      }
    }
    return undefined;
  }

  componentDidMount(): void {
    this.setState({ currentConversation: undefined });
    this.loadConversations()
      .catch(err => console.error('Failed to load conversations', err));
    this.initUpdateLoop();

    const subscription = unreadState$.subscribe(result => {
      this.setState({ unreadCounts: result });
    });
    this.setState({ unreadSubscription: subscription });
  }

  private initUpdateLoop(): void {
    // This loop is super duper lame and should be replaced by a socket connection
    // at some point, but this is not that point...
    const loopRef = setInterval(() => {
      if (ActivityHelper.isInactive()) {
        return;
      }

      const { currentConversation, loadingChat } = this.state;
      if (!loadingChat && currentConversation) {
        this.updateMessages(this.state.currentConversation, { onlyUpdateOnChanges: true })
          .catch(err => console.error('failed to update messages', err));
      }
    }, Chat.UPDATE_INTERVAL);
    this.setState({ updateLoopRef: loopRef });
  }

  componentWillUnmount(): void {
    const { updateLoopRef, unreadSubscription } = this.state;
    if (updateLoopRef) {
      clearInterval(updateLoopRef);
    }
    if (unreadSubscription) {
      unreadSubscription.unsubscribe();
    }
  }

  private loadConversations(): Promise<void> {
    const { dossier } = this.props;
    if (!dossier) {
      return Promise.reject(new NoFileSelectedError());
    }
    return Api.getDossierGroups(dossier.id)
      .then(response => {
        const dossierGroups = DossierGroup
          .fromRequest(response)
          .filter(dg => dg.shouldDisplayChat());
        this.setState({
          conversations: dossierGroups,
          loadingConversations: false,
        });
        const { initialConversationId } = this.state;
        if (initialConversationId) {
          // Load the conversation from the url
          const conversation = dossierGroups
            .find(dossierGroup => dossierGroup.groupId === initialConversationId);
          if (conversation) {
            this.handleOpenConversation(conversation);
          }
        }
      })
      .then(() => Api.listDossierRelations(dossier.id))
      .then(dossierRelations => this.setState({ dossierRelations }))
      .catch(err => {
        this.setState({ loadingConversations: false, loadingConversationsFailed: true });
        throw err;
      });
  }

  private errorHandler(err: Error, action: string): void {
    if (err instanceof NoFileSelectedError) {
      NotificationHelper.toastError(`${action} is mislukt omdat er geen dossier is geselecteerd.`);
    } else if (err instanceof MissingUserError) {
      NotificationHelper.toastError(`${action} is mislukt omdat er geen gebruiker ingelogd is.`);
    } else {
      NotificationHelper.toastError(`${action} mislukt! Ben je verbonden met het internet?`);
    }
  }

  private async sendMessage(message: string): Promise<void> {
    const { dossier } = this.props;
    const { currentConversation } = this.state;
    if (!dossier) {
      throw new NoFileSelectedError();
    }
    if (!currentConversation) {
      throw new Error('No conversation selected');
    }

    await Api.sendMessage(currentConversation.groupId, message);
  }

  private async updateMessages(conversation?: DossierGroup, { onlyUpdateOnChanges }: { onlyUpdateOnChanges?: boolean } = {}): Promise<void> {
    const { dossier } = this.props;
    const currentConversation = conversation || this.state.currentConversation;
    if (!dossier) {
      throw new NoFileSelectedError();
    }
    if (!currentConversation) {
      throw new Error('No conversation selected');
    }

    const messages = await Api.chatMessages(currentConversation.groupId);
    const changes =
      this.state.chatMessages.length !== messages.length ||
      this.state.chatMessages.map(cm => cm.read) !== messages.map(cm => cm.read);
    if (!onlyUpdateOnChanges || changes) {
      this.setState({ chatMessages: ChatMessage.fromResponse(messages), loadingChat: false });
    }
  }

  private postMessage(message: string): Promise<void> {
    this.setState({ sendingMessage: true });
    return this.sendMessage(message)
      .then(() => this.updateMessages())
      .finally(() => this.setState({ sendingMessage: false }));
  }

  filter = (conversation: DossierGroup): boolean => {
    const sanitizedKeys = this.state.searchKeys?.trim().toLowerCase();
    if (!sanitizedKeys || sanitizedKeys.length === 0) {
      return true;
    } else {
      return conversation.users.some(e => e.name.toLowerCase().includes(sanitizedKeys));
    }
  };

  handleSend = (message: string, { notifyNow }: { notifyNow?: boolean } = {}): Promise<void> => {
    const { user } = this.props;
    const { dossierRelations, currentConversation } = this.state;
    const action = 'Versturen van bericht';
    if (user) {
      return this.postMessage(message)
        .catch(err => {
          this.errorHandler(err, action);
          throw err;
        })
        .then(() => {
          if (!notifyNow) {
            return Promise.resolve();
          }
          const notifyNowMembers = this.getNotifyNowRelations(dossierRelations, currentConversation);
          const notifications = notifyNowMembers.map(member => (
            Api.notifyNow(member.memberId)
              .catch(err => {
                console.error('Notification not sent:', err);
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                if (err.key === 'UserOrEmailNotFoundRequestError') {
                  NotificationHelper.toastError(`Email niet verstuurd naar ${member.user.name}, persoon heeft geen e-mailadres`);
                } else {
                  NotificationHelper.toastError(`Email niet verstuurd naar ${member.user.name} door een onverwachte fout`);
                }
              })
          ));
          return Promise.all(notifications).then(() => undefined);
        });
    } else {
      this.errorHandler(new MissingUserError(), action);
      return Promise.reject(new MissingUserError());
    }
  };

  handleOpenConversation = (conversation?: DossierGroup): void => {
    this.setState({ currentConversation: conversation, chatMessages: [] });
    if (!conversation) {
      return;
    }
    this.updateMessages(conversation)
      .catch(err => {
        this.errorHandler(err, 'Berichten ophalen');
        this.setState({ loadingChat: false, loadingChatFailed: true });
      });
  };

  handleMobileReturn = (): void => {
    this.setState({ currentConversation: undefined });
  };

  usersToDossierRelations = (users: User[]): (DossierMember|User)[] => {
    const converted: (DossierMember | User)[] = [];
    for (const user of users) {
      const dossierRelation = this.state.dossierRelations?.find(relation => relation.user.match(user));
      converted.push(dossierRelation ?? user);
    }
    return converted;
  };

  usersToDossierRelationsStrict = (users: User[]): DossierMember[] => {
    const converted: DossierMember[] = [];
    for (const user of users) {
      const dossierRelation = this.state.dossierRelations?.find(relation => relation.user.match(user));
      if (dossierRelation) {
        converted.push(dossierRelation);
      }
    }
    return converted;
  };

  handleSearch = (value: string): void => {
    this.setState({ searchKeys: value });
  };

  getRouterPathToMember = (groupId: number): string => ERouterPath.chatConversation.replace(':groupId', `${groupId}`);

  renderMenuItem = (conversation: DossierGroup, { isBackButton }: { isBackButton?: boolean }, unreadCounts?: UnreadStatus): JSX.Element => (
    <div className={classNames('chat-menu-item', { 'chat-menu-item--back-button': isBackButton })}>
      { isBackButton && <Icon icon="back" className="chat-menu-item__back-icon" />}
      <ChatUsers members={this.usersToDossierRelations(conversation.users)} dossierGroup={conversation} small={true} unreadCount={unreadCounts && unreadCounts.chat[conversation.groupId]} />
    </div>
  );

  getNotifyNowRelations = (dossierRelations?: DossierMember[], conversation?: DossierGroup): DossierMember[] => {
    const { relation, user } = this.props;
    const isAllowedToNotify =
      relation?.role === UserRelationRoleEnum.caregiver ||
      (user && [UserAccountRoleEnum.adminCaregiver, UserAccountRoleEnum.administration].includes(user.accountRole));
    if (!dossierRelations || !isAllowedToNotify) {
      return [];
    }

    return dossierRelations
      .filter(relation => {
        const inConversation = (conversation?.users || []).some(user => user.id === relation.user.id);
        const notCaregiver = relation.role !== UserRelationRoleEnum.caregiver;
        return inConversation && notCaregiver;
      });
  };

  handleNewChat = (): void => {
    this.setState({ createNewGroup: true });
  };

  handleNewChatCreated = (newGroup: DossierGroup): void => {
    this.setState({ createNewGroup: false, conversations: [newGroup, ...this.state.conversations] });
  };

  handleArchiveCurrentGroup = (): void => {
    this.setState({ archiveGroup: this.state.currentConversation });
  };

  handleConfirmArchiveCurrentGroup = (): void => {
    const { archiveGroup, conversations } = this.state;
    if (!archiveGroup) {
      return;
    }
    Api.archiveGroup(archiveGroup.groupId)
      .then(() => {
        const filtered = conversations.filter(conversation => conversation.groupId !== archiveGroup.groupId);
        this.setState({ conversations: filtered }, () => this.handleOpenConversation(filtered[0]));
      })
      .catch(err => {
        console.error('Archiving chat failed', err);
        NotificationHelper.toastError('Archiveren van chat mislukt door een onverwachte fout');
      })
      .finally(() => this.setState({ archiveGroup: undefined }));
  };

  handleAddedPersonToGroup = (person: DossierMember): void => {
    const { currentConversation } = this.state;
    currentConversation?.users.push(person.user);
    this.setState({ addPersonToGroup: false, currentConversation });
  };

  handleAddPersonToGroup = (): void => {
    this.setState({ addPersonToGroup: true });
  };

  handleCloseAddPersonToGroup = (): void => {
    this.setState({ addPersonToGroup: false });
  };

  handleRemovedPersonFromGroup = (person: DossierMember): void => {
    const { currentConversation } = this.state;
    if (!currentConversation) {
      return;
    }
    const users = currentConversation.users.filter(user => user.id !== person.user.id);
    currentConversation.users = users;
    this.setState({ removePersonFromGroup: false, currentConversation });
  };

  handleRemovePersonFromGroup = (): void => {
    this.setState({ removePersonFromGroup: true });
  };

  handleCloseRemovePersonFromGroup = (): void => {
    this.setState({ removePersonFromGroup: false });
  };

  render(): ReactNode {
    const {
      chatMessages,
      sendingMessage,
      loadingChat,
      loadingChatFailed,
      conversations,
      loadingConversations,
      loadingConversationsFailed,
      unreadCounts,
      currentConversation,
      dossierRelations,
      createNewGroup,
      archiveGroup,
      addPersonToGroup,
      removePersonFromGroup,
    } = this.state;

    const { dossier } = this.props;
    if (!dossier) {
      NotificationHelper.toastError('Chat kan niet geladen worden... Geen dossier geselecteerd');
    }

    const defaults = { className: 'chat__column-item' };

    const splitLayoutSidebarItems: ISplitLayoutItem[] = conversations.filter(this.filter).map(conversation => ({
      ...defaults,
      element: this.renderMenuItem(conversation, {}, unreadCounts),
      mobileBackButtonElement: this.renderMenuItem(conversation, { isBackButton: true }),
      onSelect: () => this.handleOpenConversation(conversation),
      routerPath: this.getRouterPathToMember(conversation.groupId),
    }));

    splitLayoutSidebarItems.unshift({
      ...defaults,
      element: <Search onSearch={this.handleSearch}/>,
      className: 'chat__column-item--search'
    });

    if (loadingConversations) {
      splitLayoutSidebarItems.push({
        ...defaults,
        element: <Spinner className="chat__column-item--info" animation="border" variant="primary" title="loading..." />
      });
    }

    if (loadingConversationsFailed) {
      splitLayoutSidebarItems.push({
        ...defaults,
        element: <Alert className="chat__column-item--info" variant="danger">Laden van gesprekken mislukt</Alert>
      });
    }

    if (!loadingConversations && !loadingConversationsFailed && conversations.length === 0) {
      splitLayoutSidebarItems.push({
        ...defaults,
        element: <Alert className="chat__column-item--info" variant="light">Er zijn geen beschikbare gesprekken.</Alert>
      });
    }

    const mayEditGroups = !dossier?.archived && (
      (this.props.relation && this.props.relation.role === UserRelationRoleEnum.caregiver) ||
      (this.props.user && [UserAccountRoleEnum.administration, UserAccountRoleEnum.adminCaregiver].includes(this.props.user.accountRole))
    );
    const isAdminChat = currentConversation?.type === EDossierGroupType.oneToAdmin;
    if (mayEditGroups) {
      splitLayoutSidebarItems.push({
        ...defaults,
        className: 'chat__column-item--new-group-button-placeholder',
        element: <div className="chat__new-group-button-placeholder" />,
      });
      splitLayoutSidebarItems.push({
        ...defaults,
        className: 'chat__column-item--new-group-button-container',
        element: <Button className="chat__column-item--new-group-button" onClick={this.handleNewChat}>Nieuwe chat</Button>,
      });
    }

    const notifyNowMembers = this.getNotifyNowRelations(dossierRelations, currentConversation);

    return (
      <DashboardLayout className="chat" fullScreenMobile={!!currentConversation}>
        <h1 className="page-title">Chat</h1>
        <SplitSubLayout items={splitLayoutSidebarItems} rootRouterPath={ERouterPath.chat} noPadding={true} onBack={this.handleMobileReturn} skipLoadRedirectWhenOnlyOneItem={true}>
          <div className={'chat__chat-window chat-window'}>
            {
              currentConversation ?
                <>
                  <ChatHeader conversation={currentConversation} dossierRelations={dossierRelations ?? []} onMobileReturn={this.handleMobileReturn}></ChatHeader>
                  {dossier ? <ChatContainer
                    className="chat-window__chat-container"
                    chatMessages={chatMessages}
                    name={currentConversation.type === EDossierGroupType.custom ? currentConversation.name : undefined}
                    conversationType={currentConversation.type}
                    doNotDisplayUserNames={[EDossierGroupType.oneResponsiblePartyToCaregiver, EDossierGroupType.oneRelationToCaregiver, EDossierGroupType.oneClientToCaregiver].includes(currentConversation.type) && currentConversation.users.length <= 2}
                    dossierId={dossier.id}
                    loading={loadingChat}
                    error={loadingChatFailed}
                    infoMessage={isAdminChat ? 'Deze chatgroep is alleen voor administratieve zaken. Voor berichten met de behandelaar kan (een van) de andere chatgroep(en) gebruikt worden.' : undefined}
                  /> : null}
                  { !dossier?.archived && <ChatSendMessage className="chat-window__send-message" onSend={this.handleSend} sendingMessage={sendingMessage} notifyNowMembers={notifyNowMembers} /> }
                </> :
                <div className="chat-window__info">
                  <Icon icon="chat" className="chat-window__info-icon" />
                  <div>Je hebt nog geen gesprek geselecteerd</div>
                </div>
            }

            { (mayEditGroups && !isAdminChat) &&
              <div className="chat-menu-dropdown">
                <button className="chat-menu-dropdown__button"></button>
                <div className="chat-menu-dropdown__content">
                  <div className="chat-menu-dropdown__item" onClick={this.handleRemovePersonFromGroup}>Persoon verwijderen</div>
                  <div className="chat-menu-dropdown__item" onClick={this.handleAddPersonToGroup}>Persoon toevoegen</div>
                  <div className="chat-menu-dropdown__item" onClick={this.handleArchiveCurrentGroup}>Groep archiveren</div>
                </div>
              </div>
            }
          </div>
        </SplitSubLayout>

        { createNewGroup && <NewGroupForm onCreated={this.handleNewChatCreated} onCancel={() => this.setState({ createNewGroup: false })} /> }
        {
          archiveGroup &&
          <ConfirmModal title="Groep archiveren" body={<>
            Weet je zeker dat je de groep <strong>{archiveGroup.name}</strong> wilt archiveren?<br/>
            <br/>
            <i>De conversatie zal verdwijnen en niet meer aangepast kunnen worden. Er is op dit moment geen mogelijkheid om gearchiveerde chats te bekijken of terug te halen.</i>
          </>}
          onCancel={() => this.setState({ archiveGroup: undefined })}
          onConfirm={() => this.handleConfirmArchiveCurrentGroup()}
          confirmButtonStyle="danger"
          confirmLabel="Groep archiveren"
          />
        }
        {
          (addPersonToGroup && currentConversation) &&
          <AddPersonToGroupForm
            groupId={currentConversation.groupId}
            currentMembers={this.usersToDossierRelationsStrict(currentConversation.users)}
            onCancel={this.handleCloseAddPersonToGroup}
            onAdded={this.handleAddedPersonToGroup}
          />
        }
        {
          (removePersonFromGroup && currentConversation) &&
          <RemovePersonFromGroupForm
            groupId={currentConversation.groupId}
            members={this.usersToDossierRelationsStrict(currentConversation.users)}
            onCancel={this.handleCloseRemovePersonFromGroup}
            onRemoved={this.handleRemovedPersonFromGroup}
          />
        }
      </DashboardLayout>
    );
  }
}

export default connect((state: ApplicationState) => ({
  dossier: state.session.dossier,
  relation: state.session.relation,
  user: state.session.user,
}))(withRouter(Chat));
